import { styled } from "@mui/material/styles";
import { Avatar, Box, DialogContent, Tabs, Typography } from "@mui/material";
import { semantic, hexToRGBA } from "../../../common/theme/brandTokens";

interface StyledBoxProps {
  isActive?: boolean;
}

export const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<StyledBoxProps>(({ isActive }) => ({
  display: isActive ? "flex" : "none",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
}));


export const StyledTypography = styled(Typography)(() => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}));

export const SourceButton = styled("button")(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  background: "transparent",
  borderRadius: 5,
  padding: "0 8px",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  cursor: "pointer",
  fontSize: 12,
  height: 25,
  minHeight: 25,
  color: theme.palette.text.secondary,
  "&:hover": {
    background: theme.palette.action.hover,
  },
}));

export const AvatarStack = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));

export const SourceAvatar = styled(Avatar)(() => ({
  width: 16,
  height: 16,
  fontSize: 10,
  border: "1px solid white",
  marginLeft: -4,
  "& img": {
    objectFit: "contain",
    padding: "2px",
  },
}));

interface StackedAvatarProps {
  zIndexValue: number;
}

export const StackedSourceAvatar = styled(SourceAvatar, {
  shouldForwardProp: (prop) => prop !== "zIndexValue",
})<StackedAvatarProps>(({ zIndexValue }) => ({
  zIndex: zIndexValue,
}));

export const CountSourceAvatar = styled(SourceAvatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  fontSize: 10,
}));

export const GuidelineList = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

export const GuidelineItem = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "36px 1fr",
  alignItems: "start",
  gap: theme.spacing(1.5),
}));

export const GuidelineImage = styled("img")(({ theme }) => ({
  width: 30,
  height: 30,
  borderRadius: "50%",
  objectFit: "cover",
}));

export const MetadataSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  marginLeft: theme.spacing(3),
  marginRight: theme.spacing(3),
  marginTop: theme.spacing(2),
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
  flexShrink: 0,
}));

export const MetadataHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
}));

export const MetadataTitle = styled(Typography)(() => ({
  fontWeight: 600,
}));

export const MetadataChips = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

export const SourceLink = styled("a")(() => ({
  color: "inherit",
  textDecoration: "underline",
}));

export const LoadingDialogContent = styled(DialogContent)(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: 200,
}));

export const MainDialogContent = styled(DialogContent)(() => ({
  display: "flex",
  flexDirection: "column",
  height: "80vh",
  padding: 0,
  overflow: "hidden",
}));

export const OcrDisclaimer = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1, 3),
  fontSize: "0.7rem",
  lineHeight: 1.4,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.action.hover,
  borderBottom: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
}));

export const DocumentTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  "& .MuiTabs-indicator": {
    backgroundColor: theme.palette.text.primary,
    height: 2,
  },
  "& .MuiTab-root": {
    textTransform: "capitalize",
    color: theme.palette.text.primary,
    minWidth: 0,
    maxWidth: 250,
    fontSize: "0.875rem",
    paddingInline: theme.spacing(1.5),
    paddingBlock: theme.spacing(1),
    marginInline: theme.spacing(0.25),
    lineHeight: 1.2,
  },
  "& .MuiTab-root.Mui-selected": {
    color: theme.palette.text.primary,
  },
}));

export const TabLabel = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  maxWidth: "100%",
}));

export const TabLogo = styled("img")(() => ({
  width: 20,
  height: 20,
  borderRadius: "50%",
  objectFit: "contain",
  flexShrink: 0,
}));

export const TabText = styled(Typography)(() => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "0.875rem",
}));

export const PageTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  paddingInline: theme.spacing(0.5),
  minHeight: 30,
  width: "100%",
  "& .MuiTabs-indicator": {
    backgroundColor: theme.palette.text.primary,
    height: 2,
  },
  "& .MuiTab-root": {
    textTransform: "capitalize",
    color: theme.palette.text.primary,
    minWidth: 0,
    fontSize: "0.875rem",
    paddingInline: theme.spacing(0.75),
    paddingBlock: 0,
    marginInline: theme.spacing(0.25),
    lineHeight: 1.2,
  },
  "& .MuiTab-root.Mui-selected": {
    color: theme.palette.text.primary,
  },
}));

export const SingleChunkContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export const ChunkListItem = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1, 2),
}));

export const PageContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(1, 2),
}));

export const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
}));

export const ChunkTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  marginBottom: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

export const ScrollableContent = styled(Box)(() => ({
  flex: 1,
  overflowY: "auto",
  overflowX: "auto",
  paddingLeft: 24,
  paddingRight: 24,
  paddingBottom: 16,
  "&::-webkit-scrollbar": {
    width: "8px",
    height: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(128,128,128,0.3)",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "rgba(128,128,128,0.5)",
  },
  "& table": {
    minWidth: "100%",
  },
  // Highlight styles for chunk citations
  "& mark": {
    backgroundColor: hexToRGBA(semantic.warning.main, 0.4),
    padding: "2px 4px",
    borderRadius: "2px",
    fontWeight: 500,
  },
}));

export const ContextBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  opacity: 0.6,
  color: theme.palette.text.secondary,
  "& p": {
    lineHeight: 1.8,
  },
}));

export const HighlightContainer = styled("div")(() => ({
  marginBottom: "16px",
}));

export const HighlightBox = styled(Box)(() => ({
  "& p, & li, & h1, & h2, & h3, & h4, & h5, & h6, & td, & th, & code, & pre, & span, & a, & strong, & em": {
    backgroundColor: hexToRGBA(semantic.warning.main, 0.4),
    boxShadow: `0 0 0 2px ${hexToRGBA(semantic.warning.main, 0.4)}`,
  },
  "& p": {
    lineHeight: 1.8,
    marginBottom: 0,
    display: "inline",
    padding: "2px 0",
  },
  "& > *": {
    display: "inline",
  },
}));
